import {
  Button,
  Card,
  Chip,
  Input,
  Table,
} from '@heroui/react'
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { ExternalLink, Trash2 } from 'lucide-react'
import * as React from 'react'
import { z } from 'zod'
import { toast } from 'sonner'
import { authApiClient } from '~/lib/api-client'
import { useDebounce } from 'use-debounce'
import { DeleteConfirmModal } from './-components/delete-confirm-modal'

const rankingsSearchSchema = z.object({
  q: z.string().optional().catch(''),
  page: z.number().catch(1),
  pageSize: z.number().catch(20),
  year: z.number().optional().catch(undefined),
  snapshotType: z.string().optional().catch(''),
})

export const Route = createFileRoute('/admin/rankings')({
  validateSearch: rankingsSearchSchema,
  component: RankingsPage,
})

function RankingsPage() {
  const { q, page, pageSize, year, snapshotType } = Route.useSearch()
  const navigate = Route.useNavigate()
  const queryClient = useQueryClient()

  const [localSearch, setLocalSearch] = React.useState(q || '')
  const [debouncedSearch] = useDebounce(localSearch, 300)
  const [deletingRanking, setDeletingRanking] = React.useState<{ id: string; title: string } | null>(null)

  React.useEffect(() => {
    if (debouncedSearch !== q) {
      navigate({ search: (prev) => ({ ...prev, q: debouncedSearch, page: 1 }) })
    }
  }, [debouncedSearch, q, navigate])

  const { data: rankingsData, isFetching } = useQuery({
    queryKey: ['admin', 'rankings', q, page, pageSize, year, snapshotType],
    queryFn: async () => {
      const res = await authApiClient.rankings.$get({
        query: {
          page: String(page),
          pageSize: String(pageSize),
          ...(year ? { year: String(year) } : {}),
          ...(snapshotType ? { snapshotType } : {}),
        } as any,
      })
      if (!res.ok) throw res
      const json = await res.json() as any
      // Client-side title filtering (API doesn't support title search natively)
      if (q && json.data) {
        const filtered = json.data.filter((r: any) =>
          (r.title || '').toLowerCase().includes(q.toLowerCase()) ||
          (r.description || '').toLowerCase().includes(q.toLowerCase())
        )
        return { ...json, data: filtered }
      }
      return json
    },
    placeholderData: keepPreviousData,
  })

  const rankings = rankingsData?.data || []

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await authApiClient.rankings[':id'].$delete({
        param: { id },
        header: { Authorization: '' },
      })
      if (!res.ok) throw new Error('删除榜单失败')
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'rankings'] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'stats'] })
      toast.success('榜单删除成功')
      setDeletingRanking(null)
    },
    onError: () => {
      toast.error('删除榜单失败')
    },
  })

  const getTypeLabel = (type: string) => {
    if (type === 'CATEGORY') return '百名店'
    if (type === 'AWARD') return '年度奖'
    return type
  }

  const getAwardColor = (level: string | null | undefined): 'warning' | 'accent' | 'default' => {
    if (level === 'GOLD') return 'warning'
    if (level === 'SILVER') return 'accent'
    if (level === 'BRONZE') return 'default'
    return 'default'
  }

  // TanStack Table
  const columns = React.useMemo(() => {
    const columnHelper = createColumnHelper<any>()
    return [
      columnHelper.accessor('title', {
        header: '榜单名称',
        cell: info => {
          const ranking = info.row.original
          return (
            <div className="flex flex-col min-w-0">
              <span className="font-bold truncate max-w-[300px]">{ranking.title || '无标题'}</span>
              <span className="text-[10px] text-muted-foreground font-mono uppercase">{ranking.id}</span>
            </div>
          )
        },
      }),
      columnHelper.accessor('snapshotType', {
        header: '类型',
        cell: info => {
          const type = info.getValue()
          return (
            <Chip size="sm" variant="soft" color={type === 'CATEGORY' ? 'success' : 'warning'}>
              {getTypeLabel(type)}
            </Chip>
          )
        },
      }),
      columnHelper.accessor('year', {
        header: '年份',
        cell: info => <span className="font-mono font-semibold">{info.getValue()}</span>,
      }),
      columnHelper.accessor('awardLevel', {
        header: '奖项等级',
        cell: info => {
          const level = info.getValue()
          return level ? (
            <Chip size="sm" variant="soft" color={getAwardColor(level)}>
              {level}
            </Chip>
          ) : (
            <span className="text-xs text-muted italic">—</span>
          )
        },
      }),
      columnHelper.accessor('totalShops', {
        header: '店铺数',
        cell: info => <span className="font-mono text-sm">{info.getValue() || 0}</span>,
      }),
      columnHelper.accessor('url', {
        header: '源地址',
        cell: info => {
          const url = info.getValue()
          return url ? (
            <a href={url} target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline inline-flex items-center gap-1">
              <ExternalLink className="size-3" />
              查看
            </a>
          ) : <span className="text-xs text-muted italic">—</span>
        },
      }),
      columnHelper.display({
        id: 'actions',
        header: () => <div className="text-right">操作</div>,
        cell: info => {
          const ranking = info.row.original
          return (
            <div className="flex justify-end">
              <Button
                variant="danger-soft"
                size="sm"
                isIconOnly
                aria-label={`删除 ${ranking.title || '榜单'}`}
                onPress={() => setDeletingRanking({ id: ranking.id, title: ranking.title || '该榜单' })}
              >
                <Trash2 className="size-4" />
              </Button>
            </div>
          )
        },
      }),
    ]
  }, [])

  const table = useReactTable({
    data: rankings,
    columns,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
  })

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">榜单管理</h1>
          <p className="text-muted mt-1 font-medium text-sm">查看和管理百名店与年度奖榜单数据。</p>
        </div>
        <div className="flex flex-col md:flex-row items-center gap-3">
          <Input
            placeholder="搜索榜单名称..."
            className="w-full md:w-56"
            value={localSearch}
            onChange={e => setLocalSearch(e.target.value)}
          />
          <select 
            className="w-full md:w-32 px-3 py-2 text-sm bg-background border border-input rounded-md ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            value={year || ''}
            onChange={e => navigate({ search: (prev) => ({ ...prev, year: e.target.value ? Number(e.target.value) : undefined, page: 1 }) })}
          >
            <option value="">所有年份</option>
            <option value="2026">2026</option>
            <option value="2025">2025</option>
            <option value="2024">2024</option>
            <option value="2023">2023</option>
          </select>
          <select 
            className="w-full md:w-32 px-3 py-2 text-sm bg-background border border-input rounded-md ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            value={snapshotType || ''}
            onChange={e => navigate({ search: (prev) => ({ ...prev, snapshotType: e.target.value || '', page: 1 }) })}
          >
            <option value="">所有类型</option>
            <option value="CATEGORY">百名店</option>
            <option value="AWARD">年度奖</option>
          </select>
        </div>
      </div>

      <Card variant="secondary" className={isFetching ? 'opacity-70 transition-opacity' : 'transition-opacity'}>
        <Table>
          <Table.ScrollContainer>
            <Table.Content aria-label="Rankings table">
              <Table.Header>
                {table.getHeaderGroups().map(headerGroup => (
                  <React.Fragment key={headerGroup.id}>
                    {headerGroup.headers.map(header => (
                      <Table.Column key={header.id} isRowHeader={header.column.id === 'title'}>
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                      </Table.Column>
                    ))}
                  </React.Fragment>
                ))}
              </Table.Header>
              <Table.Body>
                {table.getRowModel().rows.length === 0 ? (
                  <Table.Row>
                    <Table.Cell colSpan={columns.length}>
                      <div className="text-center py-8 text-muted">
                        {isFetching ? '加载中...' : '暂无榜单数据'}
                      </div>
                    </Table.Cell>
                  </Table.Row>
                ) : (
                  table.getRowModel().rows.map(row => (
                    <Table.Row key={row.id}>
                      {row.getVisibleCells().map(cell => (
                        <Table.Cell key={cell.id}>
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </Table.Cell>
                      ))}
                    </Table.Row>
                  ))
                )}
              </Table.Body>
            </Table.Content>
          </Table.ScrollContainer>
        </Table>
      </Card>

      {/* 分页控件 */}
      {rankingsData?.pagination && rankingsData.pagination.totalPages > 1 && (
        <div className="flex items-center justify-between pt-2">
          <span className="text-sm text-muted">
            共 {rankingsData.pagination.total} 条记录，第 {rankingsData.pagination.currentPage} / {rankingsData.pagination.totalPages} 页
          </span>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              isDisabled={page <= 1}
              onPress={() => navigate({ search: (prev) => ({ ...prev, page: page - 1 }) })}
            >
              上一页
            </Button>
            {Array.from({ length: Math.min(5, rankingsData.pagination.totalPages) }, (_, i) => {
              const totalPages = rankingsData.pagination.totalPages
              let pageNum: number
              if (totalPages <= 5) {
                pageNum = i + 1
              } else if (page <= 3) {
                pageNum = i + 1
              } else if (page >= totalPages - 2) {
                pageNum = totalPages - 4 + i
              } else {
                pageNum = page - 2 + i
              }
              return (
                <Button
                  key={pageNum}
                  variant={pageNum === page ? 'primary' : 'ghost'}
                  size="sm"
                  onPress={() => navigate({ search: (prev) => ({ ...prev, page: pageNum }) })}
                >
                  {pageNum}
                </Button>
              )
            })}
            <Button
              variant="ghost"
              size="sm"
              isDisabled={page >= rankingsData.pagination.totalPages}
              onPress={() => navigate({ search: (prev) => ({ ...prev, page: page + 1 }) })}
            >
              下一页
            </Button>
          </div>
        </div>
      )}

      <DeleteConfirmModal
        isOpen={!!deletingRanking}
        onClose={() => setDeletingRanking(null)}
        entityName="榜单"
        entityLabel={deletingRanking?.title}
        onConfirm={() => deletingRanking && deleteMutation.mutate(deletingRanking.id)}
        isPending={deleteMutation.isPending}
        warningText="此操作将永久删除该榜单快照数据。榜单数据由 Scraper 自动采集生成。"
      />
    </div>
  )
}
